"use client";

import * as React from "react";
import { ChevronsUpDown, ChevronLeft, Check, X, Folder } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from "@/components/ui/command";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useTenancy } from "@/contexts/TenancyContext";

import assetsSvc from "@/services/assetsService";
import * as risksSvc from "@/services/risksService";
import documentsSvc from "@/services/documentsService";
import * as legalSvc from "@/services/legalObligationsService";
import * as objectivesSvc from "@/services/objectivesService";
import * as dataTypesSvc from "@/services/dataRetentionService";
import * as privacySvc from "@/services/privacyRequestTracker";
import * as assuranceSvc from "@/services/assuranceActionsService";
import controlsSvc from "@/services/controlsService";
import { getPolicyList } from "@/services/policyService";
import { checklistService } from "@/app/pages/checklists/services/checklist-service";
import { AuditService } from "@/app/pages/auditcentre/auditService";
import { db } from "@/lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { getAllCountries } from "@/utils/country-utils";
import { MCMService } from "@/services/mcmService";

export type MCMCategoryKey =
  | "controls" | "clauses" | "assets" | "suppliers" | "risks" | "policies"
  | "configurationStandards" | "assuranceTasks" | "checklists" | "supportingDocs"
  | "privacyRequests" | "evidence" | "dataTypes" | "dataStorageLocations"
  | "legalObligations" | "issues" | "interestedParties" | "objectives"
  | "audits" | "findings" | "incidents" | "correctiveActions";

export interface ComponentLink {
  id: string;
  domain: string;
  scheme: string;
  ref: string;
  title?: string;
  relation: string;
  confidence: number;
}

interface MCMComboboxProps {
  selected: ComponentLink[];
  onChange: (links: ComponentLink[]) => void;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  initialMasterControl?: { id: string; label: string } | null;
  onMasterChange?: (control: { id: string; label: string } | null) => void;
  onChangePayload?: (payload: { controlID: string | null; linkedComponents: ComponentLink[]; standard?: string; controlIDs?: string[] }) => void;
  allowedCategories?: MCMCategoryKey[];
  requirePrimary?: boolean;
  targetControlIds?: string[];
  targetControls?: Array<{ id: string; label?: string }>;
  pinnedEntity?: Partial<ComponentLink>;
}

interface MCMItem { id: string; label: string; description?: string; domain: string; standard?: string; meta?: Record<string, any>; }

const CATEGORY_DEFS: { key: MCMCategoryKey; title: string; domain: string }[] = [
  { key: "controls", title: "Controls", domain: "Control" },
  { key: "clauses", title: "Clauses", domain: "Standard" },
  { key: "assets", title: "Assets", domain: "Asset" },
  { key: "suppliers", title: "Suppliers", domain: "Supplier" },
  { key: "risks", title: "Risks", domain: "Risk" },
  { key: "policies", title: "Policies", domain: "Policy" },
  { key: "configurationStandards", title: "Configuration Standards", domain: "Standard" },
  { key: "assuranceTasks", title: "Assurance Tasks", domain: "Assurance" },
  { key: "checklists", title: "Checklists", domain: "Checklist" },
  { key: "supportingDocs", title: "Supporting Docs", domain: "Document" },
  { key: "privacyRequests", title: "Privacy Requests", domain: "PrivacyRequest" },
  { key: "evidence", title: "Evidence", domain: "Evidence" },
  { key: "dataTypes", title: "Data Types", domain: "DataType" },
  { key: "dataStorageLocations", title: "Data Storage Locations", domain: "DataStorage" },
  { key: "legalObligations", title: "Legal Obligations", domain: "LegalObligation" },
  { key: "issues", title: "Issues (Int/Ext)", domain: "Issue" },
  { key: "interestedParties", title: "Interested Parties", domain: "InterestedParty" },
  { key: "objectives", title: "Objectives", domain: "Objective" },
  { key: "audits", title: "Audits", domain: "Audit" },
  { key: "findings", title: "Findings", domain: "Finding" },
  { key: "incidents", title: "Incidents", domain: "Incident" },
  { key: "correctiveActions", title: "Corrective Actions", domain: "CorrectiveAction" },
];

function getRelation(domain: string): string {
  // Local overrides for domains not covered by service defaults
  if (domain === "Assurance") return "assures";
  return MCMService.getRelationForDomain(domain);
}

function toLink(item: MCMItem): ComponentLink {
  const domain = item.domain;
  return {
    id: `internal:${item.id}`,
    domain,
    scheme: "internal",
    ref: item.id,
    title: item.label,
    relation: getRelation(domain),
    confidence: 0.7,
  };
}

export const MCMCombobox: React.FC<MCMComboboxProps> = ({ selected, onChange, placeholder, className, fullWidth = true, initialMasterControl = null, onMasterChange, onChangePayload, allowedCategories, requirePrimary = true, targetControlIds, targetControls, pinnedEntity }) => {
  const { activeTenancy, isModuleEnabled } = useTenancy();
  const [open, setOpen] = React.useState(false);
  const [view, setView] = React.useState<"categories" | "items">("categories");
  const [activeCategory, setActiveCategory] = React.useState<MCMCategoryKey | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<MCMItem[]>([]);
  const [query, setQuery] = React.useState("");
  const [masterControl, setMasterControl] = React.useState<MCMItem | null>(initialMasterControl ? { id: initialMasterControl.id, label: initialMasterControl.label, domain: "Control" } : null);
  const [selectedFrameworks, setSelectedFrameworks] = React.useState<string[]>([]);
  const [riskFilters, setRiskFilters] = React.useState<{ ratings: string[]; categories: string[]; treatments: string[] }>({ ratings: [], categories: [], treatments: [] });
  const [policyFilters, setPolicyFilters] = React.useState<{ statuses: string[]; categories: string[] }>({ statuses: [], categories: [] });
  const [assuranceFilters, setAssuranceFilters] = React.useState<{ frameworks: string[]; assignees: string[]; due: string[] }>({ frameworks: [], assignees: [], due: [] });
  const [docFilters, setDocFilters] = React.useState<{ types: string[]; statuses: string[]; classes: string[] }>({ types: [], statuses: [], classes: [] });
  const [assetFilters, setAssetFilters] = React.useState<{ categories: string[]; statuses: string[] }>({ categories: [], statuses: [] });
  const [objectiveFilters, setObjectiveFilters] = React.useState<{ frameworks: string[]; owners: string[] }>({ frameworks: [], owners: [] });
  const [checklistFilters, setChecklistFilters] = React.useState<{ statuses: string[]; standards: string[]; types: string[] }>({ statuses: [], standards: [], types: [] });
  const [privacyFilters, setPrivacyFilters] = React.useState<{ statuses: string[]; types: string[]; security: string[] }>({ statuses: [], types: [], security: [] });
  const [changePrimary, setChangePrimary] = React.useState(false);

  React.useEffect(() => {
    if (initialMasterControl) {
      setMasterControl({ id: initialMasterControl.id, label: initialMasterControl.label, domain: "Control" });
    }
  }, [initialMasterControl?.id, initialMasterControl?.label]);

  const effectiveTargetControlIds = React.useMemo(() => {
    if (requirePrimary) return masterControl?.id ? [masterControl.id] : [];
    return Array.isArray(targetControlIds) ? targetControlIds : [];
  }, [requirePrimary, masterControl?.id, targetControlIds]);

  React.useEffect(() => {
    onChangePayload?.({
      controlID: effectiveTargetControlIds[0] || null,
      linkedComponents: selected,
      standard: masterControl?.standard,
      ...(effectiveTargetControlIds?.length ? { controlIDs: effectiveTargetControlIds } : {})
    } as any);
  }, [effectiveTargetControlIds.join(','), selected, masterControl?.standard]);

  const auditSvc = React.useMemo(() => activeTenancy?.id ? new AuditService({ activeTenancy: { id: activeTenancy.id } }) : null, [activeTenancy?.id]);

  const toggle = (item: MCMItem) => {
    if (item.domain === "Control") {
      if (requirePrimary) {
        // Primary-control mode: picking a control selects/changes primary; otherwise link extra controls
        if (!masterControl || changePrimary) {
          if (!masterControl || masterControl.id !== item.id) {
            setMasterControl(item);
            onMasterChange?.({ id: item.id, label: item.label });
            if (selected.length) onChange([]);
          }
          setChangePrimary(false);
          return;
        }
        if (masterControl && item.id === masterControl.id) return;
        const link = toLink(item);
        const exists = selected.some(l => l.id === link.id);
        if (exists) onChange(selected.filter(l => l.id !== link.id));
        else onChange([...selected, link]);
        return;
      } else {
        // Non-primary mode: treat controls as linkable items like others
        const link = toLink(item);
        const exists = selected.some(l => l.id === link.id);
        if (exists) onChange(selected.filter(l => l.id !== link.id));
        else onChange([...selected, link]);
        return;
      }
    }
    if (requirePrimary && !masterControl) return;
    const link = toLink(item);
    const exists = selected.some(l => l.id === link.id);
    if (exists) onChange(selected.filter(l => l.id !== link.id));
    else onChange([...selected, link]);
  };

  const fetchCategory = React.useCallback(async (cat: MCMCategoryKey): Promise<MCMItem[]> => {
    const tenantId = activeTenancy?.id || window.companyID;
    try {
      switch (cat) {
        case "controls": {
          const controls = await controlsSvc.getAllControls(isModuleEnabled);
          return controls.map(c => ({ id: String(c.value), label: c.metadata?.controlName || c.label || String(c.value), description: c.description, domain: "Control", standard: c.standard }));
        }
        case "clauses": {
          const standards = await controlsSvc.getStandards(isModuleEnabled);
          const standardsToUse = standards.length > 0 ? standards : ["ISO 27001"]; // fallback for tenancies with no enabled modules
          const results: MCMItem[] = [];
          for (const s of standardsToUse) {
            // Prefer explicit clause datasets (e.g., ISO clause collections)
            let clauseList = [] as any[];
            try { clauseList = await controlsSvc.getClausesByStandard(s); } catch {}
            if (!Array.isArray(clauseList) || clauseList.length === 0) {
              // Fallback: filter controls by isClause flag if present
              try {
                const list = await controlsSvc.getControlsByStandard(s);
                clauseList = (list || []).filter((c: any) => c?.metadata?.isClause);
                // Final fallback: if still nothing, use all controls for the standard to avoid empty state
                if (!clauseList || clauseList.length === 0) {
                  clauseList = list || [];
                }
              } catch {}
            }
            (clauseList || []).forEach((c: any) => results.push({ id: String(c.value), label: c.label, description: c.description, domain: "Standard", standard: s }));
          }
          return results;
        }
        case "assets": {
          if (!tenantId) return [];
          const list = await assetsSvc.getAssets();
          return list.filter(a => a.componentType === "Asset").map(a => ({ id: String(a.assetID), label: a.assetDesc || String(a.assetID), domain: "Asset", meta: { category: (a as any).assetCat, status: (a as any).status } }));
        }
        case "suppliers": {
          if (!tenantId) return [];
          const list = await assetsSvc.getAssets();
          return list.filter(a => a.componentType === "Supplier").map(a => ({ id: String(a.assetID), label: a.assetDesc || String(a.assetID), domain: "Supplier", meta: { category: (a as any).assetCat, status: (a as any).status } }));
        }
        case "incidents": {
          const svc = auditSvc || (tenantId ? new AuditService({ activeTenancy: { id: tenantId } }) : null);
          const list = svc ? await svc.listStandaloneIncidents() : [];
          return list.map(i => ({ id: String(i.id), label: i.summary || String(i.id), domain: "Incident", meta: { status: (i as any).status, owner: (i as any).owner } }));
        }
        case "risks": {
          if (!tenantId) return [];
          const list = await risksSvc.getAllRisks(tenantId);
          return list.map(r => ({ id: String(r.id || r.riskID), label: r.description || String(r.riskID), domain: "Risk", meta: { riskRating: r.riskRating, category: r.category, treatmentRequired: r.treatmentRequired } }));
        }
        case "policies": {
          if (!tenantId) return [];
          const list = await getPolicyList(tenantId);
          return list.map(p => ({ id: String(p.id), label: p.policy_name || p.name || String(p.id), domain: "Policy", meta: { policy_status: p.policy_status || p.status, policyCategory: p.policyCategory || p.category } }));
        }
        case "assuranceTasks": {
          if (!tenantId) return [];
          const list = await assuranceSvc.getAllTasks(tenantId);
          return list.map(t => {
            const fwSet = new Set<string>();
            Object.values(t?.checkDetail?.framework || {}).forEach((x: any) => { if (x?.frameName) fwSet.add(String(x.frameName)); });
            return {
              id: String(t.assID),
              label: t.checkDetail?.check || String(t.assID),
              domain: "Assurance",
              meta: {
                frameworks: Array.from(fwSet),
                assignedTo: t?.checkDetail?.assignedTo || "",
                nextCheck: t?.checkFrequency?.nextCheck || null,
              }
            } as MCMItem;
          });
        }
        case "checklists": {
          if (!tenantId) return [];
          const list = await checklistService.getChecklists(tenantId);
          return list.map(c => ({ id: String((c as any).id), label: c.title || String((c as any).id), domain: "Checklist", meta: { status: (c as any).status, standard: (c as any).standard, standards: (c as any).standards, isAssuranceTask: (c as any).isAssuranceTask } }));
        }
        case "supportingDocs": {
          if (!tenantId) return [];
          const list = await documentsSvc.getDocuments(tenantId);
          return list.map(d => ({ id: String(d.docID || d.id), label: d.fileName || String(d.docID || d.id), domain: "Document", meta: { documentType: (d as any).documentType || (d as any).type, status: (d as any).status, classification: (d as any).classification } }));
        }
        case "privacyRequests": {
          if (!tenantId) return [];
          const list = await privacySvc.getPrivacyRequestList(tenantId);
          return list.map(pr => ({ id: String(pr.requestID || pr.id), label: `${pr.typeOfRequest}: ${pr.name || pr.email}`, domain: "PrivacyRequest", meta: { status: (pr as any).status, typeOfRequest: (pr as any).typeOfRequest, security: (pr as any).security } }));
        }
        case "dataTypes": {
          const list = await dataTypesSvc.getDataTypes();
          return list.map(dt => ({ id: String(dt.dataTypeID || dt.id), label: dt.dataType || String(dt.dataTypeID || dt.id), domain: "DataType" }));
        }
        case "dataStorageLocations": {
          return getAllCountries().map(c => ({ id: c.value, label: c.key, domain: "DataStorage" }));
        }
        case "legalObligations": {
          // Support both legacy and new services (two files exist: .ts and .tsx)
          if (!tenantId) return [];
          const legalAny: any = legalSvc as any;
          let list: any[] = [];
          if (typeof legalAny.getLegalObligations === 'function') {
            list = await legalAny.getLegalObligations(tenantId);
          } else if (typeof legalAny.getAllLegalObligations === 'function') {
            list = await legalAny.getAllLegalObligations();
          }
          return (list || []).map((o: any) => ({ id: String(o.id), label: o.requirement || String(o.id), domain: "LegalObligation" }));
        }
        case "objectives": {
          if (!tenantId) return [];
          const list = await objectivesSvc.getAllObjectives(tenantId);
          return list.map(o => ({ id: String(o.id), label: o.objectiveText || String(o.id), domain: "Objective", meta: { frameworks: (o as any).frameworks || [], owner: (o as any).owner } }));
        }
        case "audits": {
          const list = auditSvc ? await auditSvc.listAudits() : [];
          return list.map(a => ({ id: String(a.id), label: a.type || a.scope || String(a.id), domain: "Audit" }));
        }
        case "findings": {
          const list = auditSvc ? await auditSvc.listStandaloneFindings() : [];
          return list.map(f => ({ id: String(f.id), label: f.title || f.summary || String(f.id), domain: "Finding" }));
        }
        case "correctiveActions": {
          const list = auditSvc ? await auditSvc.listStandaloneCorrectiveActions() : [];
          return list.map(a => ({ id: String(a.id), label: a.title || a.summary || String(a.id), domain: "CorrectiveAction" }));
        }
        case "issues": {
          if (!tenantId) return [];
          const regRef = doc(db, "customers", tenantId, "product", "managementSystemGuide", "issueRegister", "register");
          const oldRef = doc(db, "customers", tenantId, "product", "policyPackages", "ISO27001keyDocuments", "ismsManual");
          const [regSnap, oldSnap] = await Promise.all([getDoc(regRef), getDoc(oldRef)]);
          const items: MCMItem[] = [];
          if (regSnap.exists()) {
            const data: any = regSnap.data();
            Object.keys(data || {}).forEach(id => items.push({ id, label: data[id]?.name || id, domain: "Issue", meta: {} }));
          }
          if (oldSnap.exists()) {
            const d: any = oldSnap.data();
            const addOld = (map: any, type: string) => Object.entries(map || {}).forEach(([k, v]: any) => items.push({ id: `ISU-${k}`, label: v.issue || `${type} Issue ${k}`, domain: "Issue", meta: { issueType: type } }));
            addOld(d.issuesExternal, "External");
            addOld(d.issuesInternal, "Internal");
          }
          return items;
        }
        case "interestedParties": {
          if (!tenantId) return [];
          const ref = doc(db, "customers", tenantId, "product", "policyPackages", "ISO27001keyDocuments", "ismsManual");
          const snap = await getDoc(ref);
          if (!snap.exists()) return [];
          const data: any = snap.data();
          return Object.entries(data.partiesInterested || {}).map(([id, v]: any) => ({ id: String(id), label: v.party || String(id), domain: "InterestedParty" }));
        }
        case "configurationStandards": {
          // Use enabled standards from controls service
          const standards = await controlsSvc.getStandards(isModuleEnabled);
          return standards.map(s => ({ id: s, label: s, domain: "Standard" }));
        }
        case "evidence": {
          if (!tenantId) return [];
          const list = await documentsSvc.getDocuments(tenantId, { documentType: 'Evidence' });
          return list.map(d => ({ id: String(d.docID || d.id), label: d.fileName || String(d.docID || d.id), domain: "Evidence", meta: { documentType: (d as any).documentType || (d as any).type, status: (d as any).status, classification: (d as any).classification } }));
        }
        default:
          return [];
      }
    } catch (e) {
      console.warn("[MCMCombobox] fetchCategory error", cat, e);
      return [];
    }
  }, [activeTenancy?.id, isModuleEnabled, auditSvc]);

  const openCategory = async (cat: MCMCategoryKey) => {
    if (requirePrimary && !masterControl && cat !== "controls") {
      cat = "controls";
    }
    setActiveCategory(cat);
    setView("items");
    setLoading(true);
    setSelectedFrameworks([]);
    setRiskFilters({ ratings: [], categories: [], treatments: [] });
    setPolicyFilters({ statuses: [], categories: [] });
    setAssuranceFilters({ frameworks: [], assignees: [], due: [] });
    setDocFilters({ types: [], statuses: [], classes: [] });
    setAssetFilters({ categories: [], statuses: [] });
    setObjectiveFilters({ frameworks: [], owners: [] });
    setChecklistFilters({ statuses: [], standards: [], types: [] });
    setPrivacyFilters({ statuses: [], types: [], security: [] });
    const list = await fetchCategory(cat);
    setItems(list);
    setLoading(false);
  };

  const frameworks = React.useMemo(() => {
    if (!items?.length) return [] as string[];
    const set = new Set<string>();
    for (const it of items) {
      if (it.standard) set.add(it.standard);
    }
    return Array.from(set);
  }, [items]);

  const riskFacetValues = React.useMemo(() => {
    if (activeCategory !== "risks" || !items?.length) return { ratings: [] as string[], categories: [] as string[], treatments: [] as string[] };
    const ratings = new Set<string>();
    const categories = new Set<string>();
    const treatments = new Set<string>();
    for (const it of items) {
      const m = it.meta || {};
      if (m.riskRating) ratings.add(String(m.riskRating));
      if (m.category) categories.add(String(m.category));
      if (m.treatmentRequired) treatments.add(String(m.treatmentRequired));
    }
    return {
      ratings: Array.from(ratings),
      categories: Array.from(categories),
      treatments: Array.from(treatments),
    };
  }, [items, activeCategory]);

  const policyFacetValues = React.useMemo(() => {
    if (activeCategory !== "policies" || !items?.length) return { statuses: [] as string[], categories: [] as string[] };
    const statuses = new Set<string>();
    const categories = new Set<string>();
    for (const it of items) {
      const m = it.meta || {};
      if (m.policy_status || m.status) statuses.add(String(m.policy_status || m.status));
      if (m.policyCategory || m.category) categories.add(String(m.policyCategory || m.category));
    }
    return {
      statuses: Array.from(statuses),
      categories: Array.from(categories),
    };
  }, [items, activeCategory]);

  const assuranceFacetValues = React.useMemo(() => {
    if (activeCategory !== "assuranceTasks" || !items?.length) return { frameworks: [] as string[], assignees: [] as string[], due: [] as string[] };
    const frameworks = new Set<string>();
    const assignees = new Set<string>();
    let hasOverdue = false, hasDueSoon = false, hasNoDate = false;
    const now = new Date();
    const soon = new Date(now.getTime());
    soon.setDate(now.getDate() + 14);
    for (const it of items) {
      const m = it.meta || {};
      (Array.isArray(m.frameworks) ? m.frameworks : []).forEach((f: string) => f && frameworks.add(String(f)));
      if (m.assignedTo) assignees.add(String(m.assignedTo));
      const nc = m.nextCheck;
      let dt: Date | null = null;
      if (nc && typeof nc.toDate === 'function') dt = nc.toDate();
      else if (nc instanceof Date) dt = nc;
      else if (typeof nc === 'string' || typeof nc === 'number') dt = new Date(nc);
      if (!dt) hasNoDate = true; else if (dt < now) hasOverdue = true; else if (dt <= soon) hasDueSoon = true;
    }
    const due: string[] = [];
    if (hasOverdue) due.push("Overdue");
    if (hasDueSoon) due.push("Due soon");
    if (hasNoDate) due.push("No date");
    return { frameworks: Array.from(frameworks), assignees: Array.from(assignees), due };
  }, [items, activeCategory]);

  const docFacetValues = React.useMemo(() => {
    if (activeCategory !== "supportingDocs" || !items?.length) return { types: [] as string[], statuses: [] as string[], classes: [] as string[] };
    const types = new Set<string>();
    const statuses = new Set<string>();
    const classes = new Set<string>();
    for (const it of items) {
      const m = it.meta || {};
      if (m.documentType) types.add(String(m.documentType));
      if (m.status) statuses.add(String(m.status));
      if (m.classification) classes.add(String(m.classification));
    }
    return { types: Array.from(types), statuses: Array.from(statuses), classes: Array.from(classes) };
  }, [items, activeCategory]);

  const assetFacetValues = React.useMemo(() => {
    if (!(activeCategory === "assets" || activeCategory === "suppliers") || !items?.length) return { categories: [] as string[], statuses: [] as string[] };
    const categories = new Set<string>();
    const statuses = new Set<string>();
    for (const it of items) {
      const m = it.meta || {};
      if (m.category) categories.add(String(m.category));
      if (m.status) statuses.add(String(m.status));
    }
    return { categories: Array.from(categories), statuses: Array.from(statuses) };
  }, [items, activeCategory]);

  const objectiveFacetValues = React.useMemo(() => {
    if (activeCategory !== "objectives" || !items?.length) return { frameworks: [] as string[], owners: [] as string[] };
    const frameworks = new Set<string>();
    const owners = new Set<string>();
    for (const it of items) {
      const m = it.meta || {};
      (Array.isArray(m.frameworks) ? m.frameworks : []).forEach((f: string) => f && frameworks.add(String(f)));
      if (m.owner) owners.add(String(m.owner));
    }
    return { frameworks: Array.from(frameworks), owners: Array.from(owners) };
  }, [items, activeCategory]);

  const checklistFacetValues = React.useMemo(() => {
    if (activeCategory !== "checklists" || !items?.length) return { statuses: [] as string[], standards: [] as string[], types: [] as string[] };
    const statuses = new Set<string>();
    const standards = new Set<string>();
    let hasAssurance = false, hasChecklist = false;
    for (const it of items) {
      const m = it.meta || {};
      if (m.status) statuses.add(String(m.status));
      if (m.standard) standards.add(String(m.standard));
      if (Array.isArray(m.standards)) m.standards.forEach((s: string) => s && standards.add(String(s)));
      if (m.isAssuranceTask) hasAssurance = true; else hasChecklist = true;
    }
    const types: string[] = [];
    if (hasAssurance) types.push("Assurance Task");
    if (hasChecklist) types.push("Checklist");
    return { statuses: Array.from(statuses), standards: Array.from(standards), types };
  }, [items, activeCategory]);

  const privacyFacetValues = React.useMemo(() => {
    if (activeCategory !== "privacyRequests" || !items?.length) return { statuses: [] as string[], types: [] as string[], security: [] as string[] };
    const statuses = new Set<string>();
    const types = new Set<string>();
    const security = new Set<string>();
    for (const it of items) {
      const m = it.meta || {};
      if (m.status) statuses.add(String(m.status));
      if (m.typeOfRequest) types.add(String(m.typeOfRequest));
      if (m.security) security.add(String(m.security));
    }
    return { statuses: Array.from(statuses), types: Array.from(types), security: Array.from(security) };
  }, [items, activeCategory]);

  const filtered = React.useMemo(() => {
    const q = query.toLowerCase();
    return items
      .filter(i => !q || i.label?.toLowerCase().includes(q) || i.id.toLowerCase().includes(q))
      .filter(i => {
        if ((activeCategory === "controls" || activeCategory === "clauses") && selectedFrameworks.length > 0) {
          return i.standard && selectedFrameworks.includes(i.standard);
        }
        if (activeCategory === "risks") {
          const m = i.meta || {};
          if (riskFilters.ratings.length > 0 && (!m.riskRating || !riskFilters.ratings.includes(String(m.riskRating)))) return false;
          if (riskFilters.categories.length > 0 && (!m.category || !riskFilters.categories.includes(String(m.category)))) return false;
          if (riskFilters.treatments.length > 0 && (!m.treatmentRequired || !riskFilters.treatments.includes(String(m.treatmentRequired)))) return false;
        }
        if (activeCategory === "policies") {
          const m = i.meta || {};
          const statusVal = String(m.policy_status || m.status || "");
          const catVal = String(m.policyCategory || m.category || "");
          if (policyFilters.statuses.length > 0 && (!statusVal || !policyFilters.statuses.includes(statusVal))) return false;
          if (policyFilters.categories.length > 0 && (!catVal || !policyFilters.categories.includes(catVal))) return false;
        }
        if (activeCategory === "assuranceTasks") {
          const m = i.meta || {};
          if (assuranceFilters.frameworks.length > 0) {
            const list = Array.isArray(m.frameworks) ? m.frameworks.map((x: any) => String(x)) : [];
            if (!list.some((f: string) => assuranceFilters.frameworks.includes(f))) return false;
          }
          if (assuranceFilters.assignees.length > 0 && (!m.assignedTo || !assuranceFilters.assignees.includes(String(m.assignedTo)))) return false;
          if (assuranceFilters.due.length > 0) {
            const now = new Date();
            const soon = new Date(now.getTime());
            soon.setDate(now.getDate() + 14);
            const nc = m.nextCheck;
            let dt: Date | null = null;
            if (nc && typeof nc.toDate === 'function') dt = nc.toDate();
            else if (nc instanceof Date) dt = nc;
            else if (typeof nc === 'string' || typeof nc === 'number') dt = new Date(nc);
            const isOverdue = !!dt && dt < now;
            const isDueSoon = !!dt && dt >= now && dt <= soon;
            const isNoDate = !dt;
            const matches = (
              (assuranceFilters.due.includes("Overdue") && isOverdue) ||
              (assuranceFilters.due.includes("Due soon") && isDueSoon) ||
              (assuranceFilters.due.includes("No date") && isNoDate)
            );
            if (!matches) return false;
          }
        }
        if (activeCategory === "supportingDocs") {
          const m = i.meta || {};
          if (docFilters.types.length > 0 && (!m.documentType || !docFilters.types.includes(String(m.documentType)))) return false;
          if (docFilters.statuses.length > 0 && (!m.status || !docFilters.statuses.includes(String(m.status)))) return false;
          if (docFilters.classes.length > 0 && (!m.classification || !docFilters.classes.includes(String(m.classification)))) return false;
        }
        if (activeCategory === "assets" || activeCategory === "suppliers") {
          const m = i.meta || {};
          if (assetFilters.categories.length > 0 && (!m.category || !assetFilters.categories.includes(String(m.category)))) return false;
          if (assetFilters.statuses.length > 0 && (!m.status || !assetFilters.statuses.includes(String(m.status)))) return false;
        }
        if (activeCategory === "objectives") {
          const m = i.meta || {};
          if (objectiveFilters.frameworks.length > 0) {
            const list = Array.isArray(m.frameworks) ? m.frameworks.map((x: any) => String(x)) : [];
            if (!list.some((f: string) => objectiveFilters.frameworks.includes(f))) return false;
          }
          if (objectiveFilters.owners.length > 0 && (!m.owner || !objectiveFilters.owners.includes(String(m.owner)))) return false;
        }
        if (activeCategory === "checklists") {
          const m = i.meta || {};
          if (checklistFilters.statuses.length > 0 && (!m.status || !checklistFilters.statuses.includes(String(m.status)))) return false;
          if (checklistFilters.standards.length > 0) {
            const list = [m.standard, ...(Array.isArray(m.standards) ? m.standards : [])].filter(Boolean).map((x: any) => String(x));
            if (!list.some((s: string) => checklistFilters.standards.includes(s))) return false;
          }
          if (checklistFilters.types.length > 0) {
            const t = m.isAssuranceTask ? "Assurance Task" : "Checklist";
            if (!checklistFilters.types.includes(t)) return false;
          }
        }
        if (activeCategory === "privacyRequests") {
          const m = i.meta || {};
          if (privacyFilters.statuses.length > 0 && (!m.status || !privacyFilters.statuses.includes(String(m.status)))) return false;
          if (privacyFilters.types.length > 0 && (!m.typeOfRequest || !privacyFilters.types.includes(String(m.typeOfRequest)))) return false;
          if (privacyFilters.security.length > 0 && (!m.security || !privacyFilters.security.includes(String(m.security)))) return false;
        }
        return true;
      });
  }, [items, query, selectedFrameworks, activeCategory, riskFilters, policyFilters, assuranceFilters, docFilters, assetFilters, objectiveFilters, checklistFilters, privacyFilters]);

  const renderDropdownMulti = (
    label: string,
    values: string[],
    selected: string[],
    onToggle: (v: string) => void,
    onClear: () => void
  ) => {
    if (!values || values.length === 0) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 px-2">
            {label}{selected.length ? ` (${selected.length})` : ""}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-[200px] max-h-[320px]">
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {values.map((v) => (
            <DropdownMenuCheckboxItem key={`${label}-${v}`} checked={selected.includes(v)} onCheckedChange={() => onToggle(v)}>
              {v}
            </DropdownMenuCheckboxItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onClear}>Clear</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderFiltersRow = () => {
    if (!activeCategory) return null;
    const row: React.ReactNode[] = [];
    let hasAny = false;
    if ((activeCategory === "controls" || activeCategory === "clauses")) {
      row.push(renderDropdownMulti("Framework", frameworks, selectedFrameworks, (v) => setSelectedFrameworks(selectedFrameworks.includes(v) ? selectedFrameworks.filter(x => x !== v) : [...selectedFrameworks, v]), () => setSelectedFrameworks([])));
      hasAny = hasAny || selectedFrameworks.length > 0;
    }
    if (activeCategory === "risks") {
      row.push(renderDropdownMulti("Rating", riskFacetValues.ratings, riskFilters.ratings, (v) => setRiskFilters({ ...riskFilters, ratings: riskFilters.ratings.includes(v) ? riskFilters.ratings.filter(x => x !== v) : [...riskFilters.ratings, v] }), () => setRiskFilters({ ...riskFilters, ratings: [] })));
      row.push(renderDropdownMulti("Category", riskFacetValues.categories, riskFilters.categories, (v) => setRiskFilters({ ...riskFilters, categories: riskFilters.categories.includes(v) ? riskFilters.categories.filter(x => x !== v) : [...riskFilters.categories, v] }), () => setRiskFilters({ ...riskFilters, categories: [] })));
      row.push(renderDropdownMulti("Treatment", riskFacetValues.treatments, riskFilters.treatments, (v) => setRiskFilters({ ...riskFilters, treatments: riskFilters.treatments.includes(v) ? riskFilters.treatments.filter(x => x !== v) : [...riskFilters.treatments, v] }), () => setRiskFilters({ ...riskFilters, treatments: [] })));
      hasAny = hasAny || riskFilters.ratings.length + riskFilters.categories.length + riskFilters.treatments.length > 0;
    }
    if (activeCategory === "policies") {
      row.push(renderDropdownMulti("Status", policyFacetValues.statuses, policyFilters.statuses, (v) => setPolicyFilters({ ...policyFilters, statuses: policyFilters.statuses.includes(v) ? policyFilters.statuses.filter(x => x !== v) : [...policyFilters.statuses, v] }), () => setPolicyFilters({ ...policyFilters, statuses: [] })));
      row.push(renderDropdownMulti("Category", policyFacetValues.categories, policyFilters.categories, (v) => setPolicyFilters({ ...policyFilters, categories: policyFilters.categories.includes(v) ? policyFilters.categories.filter(x => x !== v) : [...policyFilters.categories, v] }), () => setPolicyFilters({ ...policyFilters, categories: [] })));
      hasAny = hasAny || policyFilters.statuses.length + policyFilters.categories.length > 0;
    }
    if (activeCategory === "assuranceTasks") {
      row.push(renderDropdownMulti("Framework", assuranceFacetValues.frameworks, assuranceFilters.frameworks, (v) => setAssuranceFilters({ ...assuranceFilters, frameworks: assuranceFilters.frameworks.includes(v) ? assuranceFilters.frameworks.filter(x => x !== v) : [...assuranceFilters.frameworks, v] }), () => setAssuranceFilters({ ...assuranceFilters, frameworks: [] })));
      row.push(renderDropdownMulti("Assignee", assuranceFacetValues.assignees, assuranceFilters.assignees, (v) => setAssuranceFilters({ ...assuranceFilters, assignees: assuranceFilters.assignees.includes(v) ? assuranceFilters.assignees.filter(x => x !== v) : [...assuranceFilters.assignees, v] }), () => setAssuranceFilters({ ...assuranceFilters, assignees: [] })));
      row.push(renderDropdownMulti("Due", assuranceFacetValues.due, assuranceFilters.due, (v) => setAssuranceFilters({ ...assuranceFilters, due: assuranceFilters.due.includes(v) ? assuranceFilters.due.filter(x => x !== v) : [...assuranceFilters.due, v] }), () => setAssuranceFilters({ ...assuranceFilters, due: [] })));
      hasAny = hasAny || assuranceFilters.frameworks.length + assuranceFilters.assignees.length + assuranceFilters.due.length > 0;
    }
    if (activeCategory === "supportingDocs") {
      row.push(renderDropdownMulti("Type", docFacetValues.types, docFilters.types, (v) => setDocFilters({ ...docFilters, types: docFilters.types.includes(v) ? docFilters.types.filter(x => x !== v) : [...docFilters.types, v] }), () => setDocFilters({ ...docFilters, types: [] })));
      row.push(renderDropdownMulti("Status", docFacetValues.statuses, docFilters.statuses, (v) => setDocFilters({ ...docFilters, statuses: docFilters.statuses.includes(v) ? docFilters.statuses.filter(x => x !== v) : [...docFilters.statuses, v] }), () => setDocFilters({ ...docFilters, statuses: [] })));
      row.push(renderDropdownMulti("Class", docFacetValues.classes, docFilters.classes, (v) => setDocFilters({ ...docFilters, classes: docFilters.classes.includes(v) ? docFilters.classes.filter(x => x !== v) : [...docFilters.classes, v] }), () => setDocFilters({ ...docFilters, classes: [] })));
      hasAny = hasAny || docFilters.types.length + docFilters.statuses.length + docFilters.classes.length > 0;
    }
    if (activeCategory === "assets" || activeCategory === "suppliers") {
      row.push(renderDropdownMulti("Category", assetFacetValues.categories, assetFilters.categories, (v) => setAssetFilters({ ...assetFilters, categories: assetFilters.categories.includes(v) ? assetFilters.categories.filter(x => x !== v) : [...assetFilters.categories, v] }), () => setAssetFilters({ ...assetFilters, categories: [] })));
      row.push(renderDropdownMulti("Status", assetFacetValues.statuses, assetFilters.statuses, (v) => setAssetFilters({ ...assetFilters, statuses: assetFilters.statuses.includes(v) ? assetFilters.statuses.filter(x => x !== v) : [...assetFilters.statuses, v] }), () => setAssetFilters({ ...assetFilters, statuses: [] })));
      hasAny = hasAny || assetFilters.categories.length + assetFilters.statuses.length > 0;
    }
    if (activeCategory === "objectives") {
      row.push(renderDropdownMulti("Framework", objectiveFacetValues.frameworks, objectiveFilters.frameworks, (v) => setObjectiveFilters({ ...objectiveFilters, frameworks: objectiveFilters.frameworks.includes(v) ? objectiveFilters.frameworks.filter(x => x !== v) : [...objectiveFilters.frameworks, v] }), () => setObjectiveFilters({ ...objectiveFilters, frameworks: [] })));
      row.push(renderDropdownMulti("Owner", objectiveFacetValues.owners, objectiveFilters.owners, (v) => setObjectiveFilters({ ...objectiveFilters, owners: objectiveFilters.owners.includes(v) ? objectiveFilters.owners.filter(x => x !== v) : [...objectiveFilters.owners, v] }), () => setObjectiveFilters({ ...objectiveFilters, owners: [] })));
      hasAny = hasAny || objectiveFilters.frameworks.length + objectiveFilters.owners.length > 0;
    }
    if (activeCategory === "checklists") {
      row.push(renderDropdownMulti("Status", checklistFacetValues.statuses, checklistFilters.statuses, (v) => setChecklistFilters({ ...checklistFilters, statuses: checklistFilters.statuses.includes(v) ? checklistFilters.statuses.filter(x => x !== v) : [...checklistFilters.statuses, v] }), () => setChecklistFilters({ ...checklistFilters, statuses: [] })));
      row.push(renderDropdownMulti("Standard", checklistFacetValues.standards, checklistFilters.standards, (v) => setChecklistFilters({ ...checklistFilters, standards: checklistFilters.standards.includes(v) ? checklistFilters.standards.filter(x => x !== v) : [...checklistFilters.standards, v] }), () => setChecklistFilters({ ...checklistFilters, standards: [] })));
      row.push(renderDropdownMulti("Type", checklistFacetValues.types, checklistFilters.types, (v) => setChecklistFilters({ ...checklistFilters, types: checklistFilters.types.includes(v) ? checklistFilters.types.filter(x => x !== v) : [...checklistFilters.types, v] }), () => setChecklistFilters({ ...checklistFilters, types: [] })));
      hasAny = hasAny || checklistFilters.statuses.length + checklistFilters.standards.length + checklistFilters.types.length > 0;
    }
    if (activeCategory === "privacyRequests") {
      row.push(renderDropdownMulti("Status", privacyFacetValues.statuses, privacyFilters.statuses, (v) => setPrivacyFilters({ ...privacyFilters, statuses: privacyFilters.statuses.includes(v) ? privacyFilters.statuses.filter(x => x !== v) : [...privacyFilters.statuses, v] }), () => setPrivacyFilters({ ...privacyFilters, statuses: [] })));
      row.push(renderDropdownMulti("Type", privacyFacetValues.types, privacyFilters.types, (v) => setPrivacyFilters({ ...privacyFilters, types: privacyFilters.types.includes(v) ? privacyFilters.types.filter(x => x !== v) : [...privacyFilters.types, v] }), () => setPrivacyFilters({ ...privacyFilters, types: [] })));
      row.push(renderDropdownMulti("Security", privacyFacetValues.security, privacyFilters.security, (v) => setPrivacyFilters({ ...privacyFilters, security: privacyFilters.security.includes(v) ? privacyFilters.security.filter(x => x !== v) : [...privacyFilters.security, v] }), () => setPrivacyFilters({ ...privacyFilters, security: [] })));
      hasAny = hasAny || privacyFilters.statuses.length + privacyFilters.types.length + privacyFilters.security.length > 0;
    }
    if (row.filter(Boolean).length === 0) return null;
    return (
      <div className="px-2 py-2 border-b flex flex-wrap items-center gap-2">
        {row}
        {hasAny && (
          <Button size="sm" variant="ghost" className="ml-auto h-7 text-xs" onClick={() => { if (activeCategory) openCategory(activeCategory); }}>
            Clear All
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className={cn("w-full", className)}>
            <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between" type="button">
              {masterControl ? `Control: ${masterControl.label} • ${selected.length} linked item${selected.length>1?"s":""}` : (placeholder || (requirePrimary ? "Select a control, then link items..." : "Select items..."))}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent className={cn("w-[--radix-popover-trigger-width] p-0 overflow-hidden", fullWidth ? "max-w-none" : "max-w-[560px]")} align="start">
          <div className="flex h-[420px]">
            {view === "categories" && (
              <Command className="w-full">
                <CommandInput placeholder="Search categories..." />
                {requirePrimary && !masterControl && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    Start by selecting a <span className="font-medium">Control</span> to link other items to.
                  </div>
                )}
                <CommandList className="max-h-[400px] overflow-auto" onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
                  <CommandEmpty>No categories.</CommandEmpty>
                  <CommandGroup heading="Categories">
                    {CATEGORY_DEFS.filter(c => {
                      const privacyDisabled = c.key === "privacyRequests" && !(isModuleEnabled("privacyAct") || isModuleEnabled("pa") || isModuleEnabled("Privacy Act"));
                      if (privacyDisabled) return false;
                      // DED-1447: Hide risks folder for Starter tenancies
                      const isStarterPlan = activeTenancy?.plan === 'starter' || activeTenancy?.plan === 'Starter' || activeTenancy?.plan === 'Starter Plan';
                      if (c.key === "risks" && isStarterPlan) return false;
                      return !allowedCategories || allowedCategories.includes(c.key);
                    }).map(c => {
                      const disabled = requirePrimary && !masterControl && c.key !== "controls";
                      return (
                        <CommandItem key={c.key} onSelect={() => openCategory(c.key)} className={cn(disabled && "opacity-50 pointer-events-none", !masterControl && c.key === "controls" && "bg-accent/30") }>
                        <Folder className="mr-2 h-4 w-4" />
                        <span className="mr-2">{c.title}</span>
                        {requirePrimary && !masterControl && c.key === "controls" && (
                          <Badge className="text-[10px]" variant="secondary">Start here</Badge>
                        )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            )}
            {view === "items" && (
              <Command className="w-full">
                <div className="flex items-center gap-2 p-2 border-b">
                  <Button size="sm" variant="ghost" onClick={() => { setView("categories"); setActiveCategory(null); setItems([]); setQuery(""); }}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="text-sm font-medium">
                    {CATEGORY_DEFS.find(c=>c.key===activeCategory)?.title}
                    {requirePrimary && !masterControl && activeCategory !== "controls" && (
                      <span className="ml-2 text-xs text-muted-foreground">(Select a control first)</span>
                    )}
                  </div>
                </div>
                {requirePrimary && !masterControl && activeCategory !== "controls" && (
                  <div className="px-3 py-2 text-xs text-muted-foreground">
                    Choose a <span className="font-medium">Control</span> to enable linking other items.
                  </div>
                )}
                {requirePrimary && masterControl && activeCategory === "controls" && (
                  <div className="px-3 py-2 border-b flex items-center gap-2">
                    <div className="text-xs">
                      <span className="text-muted-foreground">Primary:</span> <span className="font-medium">{masterControl.label}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-1">
                      <Button size="sm" variant={changePrimary ? "secondary" : "outline"} className="h-7 text-xs" onClick={() => setChangePrimary(false)}>Link controls</Button>
                      <Button size="sm" variant={changePrimary ? "outline" : "ghost"} className="h-7 text-xs" onClick={() => setChangePrimary(true)}>Change primary</Button>
                    </div>
                  </div>
                )}
                {!requirePrimary && !!(targetControls?.length) && (
                  <div className="px-3 py-2 border-b text-xs text-muted-foreground">
                    <span className="mr-1">Targets:</span>
                    <span className="text-foreground">
                      {targetControls.slice(0, 2).map(c => c.label || c.id).join(", ")}
                      {targetControls.length > 2 ? ` +${targetControls.length - 2} more` : ""}
                    </span>
                  </div>
                )}
                <CommandInput placeholder="Search..." value={query} onValueChange={setQuery} disabled={loading} />
                {renderFiltersRow()}
                <CommandList className="max-h-[400px] overflow-auto" onWheel={(e) => e.stopPropagation()} onTouchMove={(e) => e.stopPropagation()}>
                  {loading && <CommandEmpty>Loading...</CommandEmpty>}
                  {!loading && filtered.length === 0 && <CommandEmpty>No items.</CommandEmpty>}
                  {!loading && filtered.length > 0 && (
                    <CommandGroup>
                      {filtered.map(it => {
                        if (requirePrimary && !masterControl && it.domain !== "Control") return null;
                        const link = toLink(it);
                        const checked = selected.some(l => l.id === link.id);
                        const isPrimaryControl = masterControl && it.domain === "Control" && it.id === masterControl.id;
                        const disabledPrimaryWhenLinking = masterControl && activeCategory === "controls" && !changePrimary && isPrimaryControl;
                        return (
                          <CommandItem key={`${it.domain}:${it.id}`} value={`${it.id} ${it.label}`} onSelect={() => !disabledPrimaryWhenLinking && toggle(it)} className={cn("flex items-start gap-2", disabledPrimaryWhenLinking && "opacity-60 pointer-events-none") }>
                            <Check className={cn("h-4 w-4 mt-0.5", checked ? "opacity-100" : "opacity-0")} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{it.domain}</Badge>
                                {(it.domain === "Control" || it.domain === "Standard") && it.standard && (
                                  <Badge variant="outline" className="text-xs">{it.standard}</Badge>
                                )}
                                {isPrimaryControl && (
                                  <Badge variant="secondary" className="text-[10px]">Primary</Badge>
                                )}
                                {it.domain === "Risk" && it.meta?.riskRating && (
                                  <Badge variant="outline" className="text-xs">{String(it.meta.riskRating)}</Badge>
                                )}
                                {it.domain === "Policy" && it.meta?.policy_status && (
                                  <Badge variant="outline" className="text-xs">{String(it.meta.policy_status)}</Badge>
                                )}
                                <Badge variant="secondary" className="text-xs">{it.id}</Badge>
                              </div>
                              <div className="font-medium truncate max-w-[420px]">{it.label}</div>
                              {it.description && (
                                <div className="text-xs text-muted-foreground truncate max-w-[420px]">{it.description}</div>
                              )}
                              {it.domain === "Risk" && (it.meta?.category || it.meta?.treatmentRequired) && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {it.meta?.category && <Badge variant="outline" className="text-[10px]">{String(it.meta.category)}</Badge>}
                                  {it.meta?.treatmentRequired && <Badge variant="outline" className="text-[10px]">{String(it.meta.treatmentRequired)}</Badge>}
                                </div>
                              )}
                              {it.domain === "Policy" && it.meta?.policyCategory && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  <Badge variant="outline" className="text-[10px]">{String(it.meta.policyCategory)}</Badge>
                                </div>
                              )}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <div className="flex flex-wrap gap-1">
        {masterControl && (
          <Badge variant="outline" className="flex items-center gap-1 py-1">
            <span className="text-xs font-medium">Control</span>
            <span className="text-xs">-</span>
            <span className="text-xs truncate max-w-[220px]">{masterControl.label}</span>
            <button className="ml-1 rounded-full hover:bg-primary/20 p-0.5" onClick={(e) => { e.stopPropagation(); setMasterControl(null); onMasterChange?.(null); if (selected.length) onChange([]); }}>
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}
        {!requirePrimary && pinnedEntity && (
          <Badge variant="outline" className="flex items-center gap-1 py-1">
            <span className="text-xs font-medium">{String(pinnedEntity.domain || 'Component')}</span>
            <span className="text-xs">-</span>
            {(() => {
              const titleRaw = String(pinnedEntity.title || '').trim();
              const isAssurance = String(pinnedEntity.domain || '').toLowerCase() === 'assurance';
              const isGeneric = titleRaw.toLowerCase() === 'assurance task';
              const display = isAssurance && (isGeneric || !titleRaw) ? (pinnedEntity.ref || titleRaw || pinnedEntity.id) : (titleRaw || pinnedEntity.ref || pinnedEntity.id);
              return (
                <span className="text-xs truncate max-w-[220px]">{display}</span>
              );
            })()}
          </Badge>
        )}
        {selected.map(link => {
          const titleRaw = (link.title || '').trim();
          const isAssurance = String(link.domain || '').toLowerCase() === 'assurance';
          const isGeneric = titleRaw.toLowerCase() === 'assurance task';
          const display = isAssurance && (isGeneric || !titleRaw) ? (link.ref || titleRaw) : (titleRaw || link.ref);
          return (
            <Badge key={link.id} variant="secondary" className="flex items-center gap-1 py-1">
              <span className="text-xs font-medium">{link.domain}</span>
              <span className="text-xs">-</span>
              <span className="text-xs truncate max-w-[180px]">{display}</span>
              <button className="ml-1 rounded-full hover:bg-primary/20 p-0.5" onClick={(e) => { e.stopPropagation(); onChange(selected.filter(l => l.id !== link.id)); }}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          );
        })}
      </div>
    </div>
  );
};

export default MCMCombobox;
